import { Badge, BlockStack, Box, Card, InlineGrid, InlineStack, ProgressBar, Text } from '@shopify/polaris'
import type { BccData } from './useBccData'

function egp(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M EGP`
  if (n >= 1_000)     return `${(n / 1_000).toFixed(0)}K EGP`
  return `${n.toLocaleString('en-US', { maximumFractionDigits: 0 })} EGP`
}

function pct(n: number | null | undefined): string {
  if (n == null) return '—'
  return `${n.toFixed(1)}%`
}

type ScenarioProps = {
  label:    string
  value:    number
  target:   number
  tone:     'success' | 'critical' | 'caution'
}

function Scenario({ label, value, target, tone }: ScenarioProps) {
  const pctVal = target > 0 ? Math.min((value / target) * 100, 150) : 0
  return (
    <BlockStack gap="150">
      <InlineStack align="space-between">
        <Text as="p" variant="bodySm" tone="subdued">{label}</Text>
        <Text as="p" variant="bodySm" fontWeight="semibold"
          tone={tone === 'success' ? 'success' : tone === 'critical' ? 'critical' : undefined}
        >
          {egp(value)}
        </Text>
      </InlineStack>
      <ProgressBar progress={pctVal} tone={tone === 'critical' ? 'critical' : 'success'} size="small" />
    </BlockStack>
  )
}

type Props = { data: BccData }

export function ForecastSection({ data }: Props) {
  const { forecast, fulfillment } = data
  const tp = forecast.targetProgress
  const pr = forecast.projected

  const target       = tp?.revenueTarget ?? 0
  // Revenue booked in the period — every order placed, not only what arrived.
  // It was previously labelled "Delivered so far", which it never was (§9.9).
  const booked       = tp?.revenueActual ?? forecast.revenue.net
  const projected    = pr.projectedRevenue
  const projectedPct = target > 0 ? (projected / target) * 100 : null

  // Money still moving through fulfilment. The old version subtracted a figure
  // from itself and was therefore always zero, so the row never once appeared.
  // The real number is on the same screen, in the Money Pipeline.
  const pipeline = fulfillment.pipeline.inTransit.totalCodAmount
                 + fulfillment.pipeline.readyToShip.totalCodAmount

  // Band from the store's own daily volatility rather than a fixed ±%.
  const best     = pr.bestCase
  const worst    = pr.worstCase
  const daysLeft = forecast.pacing.workingDaysLeft
  const required = forecast.pacing.requiredDailyRevenue

  const projTone =
    projectedPct == null ? 'caution' :
    projectedPct >= 95   ? 'success' :
    projectedPct >= 80   ? 'caution' : 'critical'

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingMd">Month-end Forecast</Text>
          {projectedPct != null && (
            <Badge tone={projTone === 'success' ? 'success' : projTone === 'critical' ? 'critical' : 'attention'}>
              {`${pct(projectedPct)} of target`}
            </Badge>
          )}
        </InlineStack>

        {/* Progress rows */}
        <BlockStack gap="200">
          <InlineStack align="space-between">
            <Text as="p" variant="bodySm" tone="subdued">Monthly target</Text>
            <Text as="p" variant="bodySm" fontWeight="semibold">{target > 0 ? egp(target) : 'Not set'}</Text>
          </InlineStack>
          <InlineStack align="space-between">
            <Text as="p" variant="bodySm" tone="subdued">Booked so far</Text>
            <Text as="p" variant="bodySm" fontWeight="semibold" tone="success">{egp(booked)}</Text>
          </InlineStack>
          {pipeline > 0 && (
            <InlineStack align="space-between">
              <Text as="p" variant="bodySm" tone="subdued">In the pipeline (ready + in transit)</Text>
              <Text as="p" variant="bodySm" fontWeight="semibold">{egp(pipeline)}</Text>
            </InlineStack>
          )}
          <InlineStack align="space-between">
            <Text as="p" variant="bodySm" tone="subdued">Running at</Text>
            <Text as="p" variant="bodySm" fontWeight="semibold">
              {`${egp(forecast.pacing.dailyRevenue)}/day`}
            </Text>
          </InlineStack>
          <InlineStack align="space-between">
            <Text as="p" variant="bodySm" tone="subdued">
              {`Needed over ${daysLeft} working ${daysLeft === 1 ? 'day' : 'days'} left`}
            </Text>
            <Text
              as="p"
              variant="bodySm"
              fontWeight="semibold"
              tone={
                required == null ? undefined
                  : required > forecast.pacing.dailyRevenue ? 'critical' : 'success'
              }
            >
              {required != null ? `${egp(required)}/day` : '—'}
            </Text>
          </InlineStack>
        </BlockStack>

        {/* Projected bottom line. The forecast used to stop at revenue, leaving
            the owner to guess what survived — and its profit figure was
            operating profit, before rent and payroll (§9.4). */}
        <InlineStack align="space-between" blockAlign="center">
          <Text as="p" variant="bodySm" tone="subdued">
            {pr.fixedExpenses > 0
              ? 'Projected net profit (after fixed expenses)'
              : 'Projected net profit'}
          </Text>
          <Text
            as="p"
            variant="bodyMd"
            fontWeight="semibold"
            tone={pr.projectedNetProfit >= 0 ? 'success' : 'critical'}
          >
            {egp(pr.projectedNetProfit)}
          </Text>
        </InlineStack>

        {/* Projected total */}
        <Box
          background={projTone === 'success' ? 'bg-surface-success' : projTone === 'critical' ? 'bg-surface-critical' : 'bg-surface-caution'}
          padding="300"
          borderRadius="200"
        >
          <InlineStack align="space-between" blockAlign="center">
            <Text as="p" variant="bodyMd" tone="subdued">Projected month-end</Text>
            <Text as="p" variant="headingLg"
              tone={projTone === 'success' ? 'success' : projTone === 'critical' ? 'critical' : undefined}
            >
              {egp(projected)}
            </Text>
          </InlineStack>
        </Box>

        {/* Scenarios */}
        {target > 0 && (
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center">
              <Text as="h3" variant="headingSm">Scenarios</Text>
              {pr.volatilitySampleDays > 1 && (
                <Text as="p" variant="bodySm" tone="subdued">
                  {`from ${pr.volatilitySampleDays} days of trading`}
                </Text>
              )}
            </InlineStack>
            <InlineGrid columns={3} gap="200">
              <Scenario label="Best case"    value={best}      target={target} tone="success"  />
              <Scenario label="Expected"     value={projected} target={target} tone="caution"  />
              <Scenario label="Worst case"   value={worst}     target={target} tone="critical" />
            </InlineGrid>
            {pr.volatilitySampleDays <= 1 && (
              <Text as="p" variant="bodySm" tone="subdued">
                Not enough trading days yet to measure how much daily revenue varies,
                so the range is shown flat.
              </Text>
            )}
          </BlockStack>
        )}
      </BlockStack>
    </Card>
  )
}
